import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Lock, Mail, Eye, EyeOff, Shield, GraduationCap } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function AdminLoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loginType, setLoginType] = useState('admin');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [sendingReset, setSendingReset] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error('Please enter both email and password');
      return;
    }

    setLoading(true);
    try {
      // Try admin login first, then instructor login
      const endpoint = loginType === 'admin' ? '/admin/login' : '/instructor/login';
      const response = await axios.post(`${API_URL}${endpoint}`, {
        email: email.toLowerCase(),
        password
      });

      // Store token and user info
      localStorage.setItem('adminToken', response.data.token);
      
      if (response.data.user_type === 'instructor') {
        localStorage.setItem('adminInfo', JSON.stringify({
          ...response.data.instructor,
          is_instructor: true
        }));
        toast.success(`Welcome, ${response.data.instructor.name}!`);
      } else {
        localStorage.setItem('adminInfo', JSON.stringify(response.data.admin));
        toast.success(`Welcome back, ${response.data.admin.name}!`);
      }
      
      navigate('/dashboard');
    } catch (error) {
      console.error('Login error:', error);
      if (error.response?.status === 403) {
        toast.error('Password not set up. Check your email for the setup link or use "Forgot Password".');
      } else if (error.response?.status === 401) {
        toast.error('Invalid email or password');
      } else {
        toast.error(error.response?.data?.detail || 'Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    
    if (!resetEmail) {
      toast.error('Please enter your email address');
      return;
    }

    setSendingReset(true);
    try {
      const response = await axios.post(`${API_URL}/admin/request-password-reset`, {
        email: resetEmail.toLowerCase()
      });
      
      toast.success('If this email exists, a password reset link will be sent.');
      
      // In development, show the reset link
      if (response.data.reset_link) {
        console.log('Reset link:', response.data.reset_link);
        toast.info('Check console for reset link (dev only)', { duration: 5000 });
      }
      
      setShowForgotPassword(false);
      setResetEmail('');
    } catch (error) {
      console.error('Reset error:', error);
      toast.error('Failed to send reset email. Please try again.');
    } finally {
      setSendingReset(false);
    }
  };

  if (showForgotPassword) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="flex justify-center mb-8">
            <Link to="/public" className="flex items-center gap-2">
              <div className="flex items-center justify-center w-14 h-14 bg-[#1E3A5F] rounded-xl shadow-lg">
                <svg viewBox="0 0 40 40" className="w-10 h-10" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20 4L8 10V18C8 26.84 13.16 34.92 20 37C26.84 34.92 32 26.84 32 18V10L20 4Z" fill="#0EA5E9"/>
                  <path d="M16 18L19 21L25 15" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div className="flex flex-col">
                <span className="text-2xl font-bold text-[#1E3A5F] leading-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
                  DealLab
                </span>
                <span className="text-sm text-sky-600 font-medium -mt-0.5">CE Education</span>
              </div>
            </Link>
          </div>

          <Card className="border-slate-200 shadow-xl">
            <CardHeader className="text-center pb-4">
              <CardTitle className="text-2xl text-[#1E3A5F]" style={{ fontFamily: 'Playfair Display, serif' }}>
                Reset Password
              </CardTitle>
              <CardDescription>
                Enter your email address and we'll send you a link to reset your password.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="resetEmail">Email Address</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="resetEmail"
                      type="email"
                      placeholder="you@example.com"
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-[#1E3A5F] hover:bg-[#2a4a6f] text-white"
                  disabled={sendingReset}
                >
                  {sendingReset ? 'Sending...' : 'Send Reset Link'}
                </Button>
              </form>

              <div className="mt-4 text-center">
                <button
                  onClick={() => setShowForgotPassword(false)}
                  className="text-sm text-sky-600 hover:text-sky-700"
                >
                  ← Back to Login
                </button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <Link to="/public" className="flex items-center gap-2">
            <div className="flex items-center justify-center w-14 h-14 bg-[#1E3A5F] rounded-xl shadow-lg">
              <svg viewBox="0 0 40 40" className="w-10 h-10" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M20 4L8 10V18C8 26.84 13.16 34.92 20 37C26.84 34.92 32 26.84 32 18V10L20 4Z" fill="#0EA5E9"/>
                <path d="M16 18L19 21L25 15" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className="flex flex-col">
              <span className="text-2xl font-bold text-[#1E3A5F] leading-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
                DealLab
              </span>
              <span className="text-sm text-sky-600 font-medium -mt-0.5">CE Education</span>
            </div>
          </Link>
        </div>

        {/* Login Card */}
        <Card className="border-slate-200 shadow-xl">
          <CardHeader className="text-center pb-4">
            <Tabs value={loginType} onValueChange={setLoginType} className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-4">
                <TabsTrigger value="admin" className="flex items-center gap-2">
                  <Shield className="w-4 h-4" />
                  Admin
                </TabsTrigger>
                <TabsTrigger value="instructor" className="flex items-center gap-2">
                  <GraduationCap className="w-4 h-4" />
                  Instructor
                </TabsTrigger>
              </TabsList>
            </Tabs>
            <CardTitle className="text-2xl text-[#1E3A5F]" style={{ fontFamily: 'Playfair Display, serif' }}>
              {loginType === 'admin' ? 'Admin Login' : 'Instructor Login'}
            </CardTitle>
            <CardDescription>
              {loginType === 'admin' 
                ? 'Sign in to access the full dashboard' 
                : 'Sign in to access your courses'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10"
                    data-testid="login-email-input"
                    autoComplete="email"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">Password</Label>
                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(true)}
                    className="text-xs text-sky-600 hover:text-sky-700"
                  >
                    Forgot password?
                  </button>
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 pr-10"
                    data-testid="login-password-input"
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                className={`w-full text-white ${loginType === 'admin' ? 'bg-[#1E3A5F] hover:bg-[#2a4a6f]' : 'bg-sky-500 hover:bg-sky-600'}`}
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Signing in...
                  </div>
                ) : (
                  'Sign In'
                )}
              </Button>
            </form>

            <div className="mt-6 pt-6 border-t border-slate-200 text-center">
              <Link 
                to="/public" 
                className="text-sm text-slate-500 hover:text-[#1E3A5F] transition-colors"
              >
                ← Back to Public Website
              </Link>
            </div>
          </CardContent>
        </Card>

        <p className="text-center text-sm text-slate-500 mt-6">
          Access is restricted to authorized instructors and administrators only.
        </p>
      </div>
    </div>
  );
}
